<template>
  <div class="post-creator container">

    <input-container
      class="row"
      @updateValidity="updateBaseValidity">

      <!-- Post's Image -->
      <div
        @mouseover="lockPostInput(true)"
        @mouseout="lockPostInput(false)"
        class="col-12">

        <input-image
          ref="imageDropzone"
          inputName="media"
          id="inputMedia"
          :expectedType="getExpectedMimeType"
          :images="editFields.media"
          :localRemove="localRemove"
          @dropzoneError="dropzoneError"
          @fileChange="fileChange" />

      </div>
    
      <!-- Post's Label -->
      <div class="col-12">
        <input-text
          :rich="rich"
          inputName="label"
          variant="standardInput"
          stretched
          smaller
          :constraints="getLabelConstraints"
          :inputPlaceholder="$t('Post.title')"
          :inputValue="getLabel"
          @fieldUpdate="fieldUpdate" />

      </div>
      
      <!-- Post's Description -->
      <div class="col-12">
        <input-textarea
          :rich="rich"
          inputName="description"
          :inputPlaceholder="$t('Post.description')"
          variant="standardInput"
          stretched
          :constraints="getContentConstraints"
          :inputValue="getContent"
          @fieldUpdate="fieldUpdate" />

      </div>

      <!-- Post's URL -->
      <div class="col-12">
        <input-text
          inputName="url"
          variant="standardInput"
          stretched
          smaller
          :constraints="getUrlConstraints"
          :inputPlaceholder="$t('URL.name')"
          :inputValue="getPostUrl"
          @fieldUpdate="fieldUpdate" />

      </div>
    </input-container>

    <!-- Product specific fields -->
    <div
      v-if="isProduct"
      class="product-items">
      
      <!-- Price Input -->
      <input-container
        class="row"
        @updateValidity="updatePriceValidity">

        <div class="col">
          <input-number
            inputName="price"
            variant="standardInput"
            stretched
            smaller
            :footnote="$t('InputPost.input.price.footnote')"
            :constraints="getPriceConstraints"
            :inputPlaceholder="$t('Services.pricePlaceholder')"
            :inputValue="getPrice"
            @fieldUpdate="fieldUpdate" />

        </div>
      </input-container>

      <!-- Has Discount -->
      <div class="row mt-2 mb-2">
        <div class="col">
          <check-box
            v-if="canSetDiscount"
            @select="updateDiscountDisplay"
            :active="{hasDiscount: hasDiscount}"
            boldLabels
            spacedLabels
            customFontSize="13px"
            class="box-item"
            :label="$t('Services.discountPlaceholder')"
            :name="discountCheckboxLabel" />

        </div>
      </div>

      <!-- Discount Input & Discount Duration -->
      <div
        v-if="hasDiscount"
        class="row">

        <div class="col">
          <div class="container product-discount-params">
            <input-discount
              inputName="discount"
              :inputValue="getDiscount"
              :price="getPrice"
              :canBeChanged="true"
              @fieldUpdate="fieldUpdate"
              @updateValidity="updateDiscountValidity"/>

            <div class="col-12 p-0">
              <input-date-time
                :inputPlaceholder="$t('Services.discountValidToPlaceholder')"
                inputName="discountValidity"
                variant="standardInput"
                stretched
                smaller
                boldPholder
                :inputValue="getDiscountValidity"
                @fieldUpdate="fieldUpdate" />
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputText from '@/components/standard/input/InputText'
import InputTextarea from '@/components/standard/input/InputTextarea'
import InputImage from '@/components/standard/input/InputImage'
import InputDateTime from '@/components/standard/input/InputDateTime'
import InputNumber from '@/components/standard/input/InputNumber'
import InputDiscount from '@/components/standard/input/InputDiscount'
import InputSelect from '@/components/standard/input/InputSelect'
import FormCheckbox from '@/components/standard/form/FormCheckbox'
import apiConnector from '@/mixins/apiConnector'

import InputContainer from '@/components/standard/input/InputContainer'
import CheckBox from '@/components/standard/form/customItems/CheckBox'

import nManager from '@/assets/modules/notifications/manager'
import resolver from '@/assets/modules/error/resolver'

import cb from '@/assets/modules/constraints/constraintBuilder'

import moment from 'moment'

export default {
   data () {
    return {
      postFields: {
        discountType: 'rel'
      },
      hasDiscount: false,

      baseValidity: false,
      priceValidity: false,
      discountValidity: false
    }
  },

  methods: {
    fileChange (e) {
      this.$emit('fileChange', e)
    },

    dropzoneError (e) {
      let message = resolver.resolve(e.message)
      nManager.nokNotification(this, message)
    },

    updateBaseValidity (e) {
      this.$set(this, 'baseValidity', e.valid)
      this.emitInputValidity(e)
    },

    updatePriceValidity (e) {
      this.$set(this, 'priceValidity', e.valid)
      this.emitInputValidity(e)
    },

    updateDiscountValidity (e) {
      this.$set(this, 'discountValidity', e.valid)
      this.emitInputValidity(e)
    },

    emitInputValidity (e) {
      e = JSON.parse(JSON.stringify(e))
      e.valid = this.hasValidInputs
      this.$emit('updateValidity', e)
    },

    /**
     * Emits a lock input event - inputImage's file picker for some reason counts as a clock outside...
     * @prop {Boolean} lock If the input is locked
     */
    lockPostInput (lock) {
      this.$emit('lockPostInput', { lock })
    },

    setClearCallback (clearCallback, ...clearParams) {
      this.$refs.imageDropzone.setClearCallback(clearCallback, clearParams)
    },


    // Callback function for image uploader for when it is done...
    clearFields () {
      this.$set(this, 'postFields', {})
      this.$refs.imageDropzone.clearImages()
      this.hasDiscount = false
    },

    selectDiscountType (e) {
      this.$set(this.postFields, 'discountType', e.value)
    },

    // Prepares a GQL object, so the parent doesn't have to bother
    // With this.
    makeGqlObject () {
      // Base params
      let gqlObject = {
        id: this.postFields.id,
        url: this.postFields.url,
        label: this.postFields.label,
        type: this.type
      }

      // Type specific fields
      let add = {}
      switch (this.type) {
        case 'post':
          add = {
            content: this.postFields.description
          }
          break

        case 'product':
          add = {
            description: this.postFields.description,
            price: this.postFields.price
          }

          let discountParams = {
            discount: this.postFields.discount,
            discountValidity: this.postFields.discountValidity
          }

          if (this.hasDiscount) {
            add = Object.assign({}, add, discountParams)
          }
          break

        case 'importedProduct':
          add = {
            description: this.postFields.description,
            vendor: this.postFields.vendor,
            price: this.postFields.price,
          }
          break
      }

      gqlObject[this.type] = add

      return {gqlObject}
    },

    // So it can gather all available files from DropzoneJS
    getAllFiles () {
      return this.$refs.imageDropzone.getAllFiles()
    },

    // So it can invoke image processing for DropzoneJS
    processImages (refType = null, fefId = null, imgId = null, reupload = false) {
      this.$refs.imageDropzone.processImages(refType, fefId, imgId, reupload)
    },


    // Toggle Product's discount
    updateDiscountDisplay (e) {
      // If no price is given than don't allow discount
      if (!this.postFields.price) {
        this.hasDiscount = false
        return false
      }

      this.hasDiscount = !this.hasDiscount
      e = {
        input: e.name,
        value: !e.value
      }
      this.fieldUpdate(e)

      this.emitInputValidity({ valid: this.hasValidInputs })
    },

    // Updates post's paramaters; sends them to parent
    fieldUpdate (e) {
      // First check what fields are valid
      let val = e.value

      if (e.input === 'discountValidity' && !val) return

      this.$set(this.postFields, e.input, val)
      this.emitUpdate()
    },

    emitUpdate () {
      let {gqlObject} = this.makeGqlObject()

      // Send change event
      this.$emit('fieldUpdate', {
        fields: this.postFields,
        gqlObject
      })
    }
  },

  computed: {
    hasValidInputs () {
      if (this.isNews) {
        return this.baseValidity
      } else {
        let base = this.baseValidity && this.priceValidity
        if (this.hasDiscount) {
          return base && this.discountValidity
        }

        return base
      }
    },

    /**
     * Gets expected mime type for dropzone
     * @returns {Object} An object of {type, subType}
     */
    getExpectedMimeType () {
      return {
        type: 'image',
        subType: '*'
      }
    },

    discountCheckboxLabel () {
      return 'hasDiscount'
    },

    canSetDiscount () {
      let rtr = !!this.postFields.price
      if (!rtr) {
        // No params needed, becouse the price is not set, and it will fail
        // becouse of that.
        this.$set(this.postFields, 'hasDiscount', false)
        this.updateDiscountDisplay({})
      }
      return rtr
    },

    getLabel () {
      return this.postFields.label
    },
    getContent () {
      return this.postFields.description
    },
    getPrice () {
      return this.postFields.price
    },
    getDiscount () {
      return this.postFields.discount || null
    },
    postHasDiscount () {
      return this.getDiscount
    },
    getDiscountValidity () {
      return this.postFields.discountValidity || null
    },
    getPostUrl () {
      return this.postFields.url || null
    },

    getDiscountCheckbox () {
      if (this.postHasDiscount) {
        return {
          discount: true
        }
      }
      return {}
    },

    isProduct () {
      return this.type === 'product' || this.type === 'importedProduct'
    },

    isNews () {
      return this.type === 'news' || this.type === 'post'
    },

    // Constraints
    getLabelConstraints () {
      return cb.build('text', {onInit: true, required: true})
    },

    getContentConstraints () {
      return cb.build('text', {onInit: true, required: true})
    },

    getUrlConstraints () {
      return cb.build('url', {onInit: true, optional: true})
    },

    getPriceConstraints () {
      return cb.build('price', {onInit: true, required: true, allowed: 0})
    }
  },

  watch: {
    editFields: function () {
      this.$set(this, 'postFields', this.editFields)
      this.hasDiscount = !!this.editFields.discount
      this.emitUpdate()
    }
  },

  components: {
    'input-text': InputText,
    'input-number': InputNumber,
    'input-select': InputSelect,
    'input-textarea': InputTextarea,
    'input-image': InputImage,
    'input-date-time': InputDateTime,
    'input-discount': InputDiscount,
    'form-checkbox': FormCheckbox,
    'input-container': InputContainer,
    'check-box': CheckBox
  },

  mixins: [
    apiConnector
  ],

  props: {
    localRemove: {
      type: Boolean,
      required: false,
      default: false
    },
    rich: {
      type: Boolean,
      required: false,
      default: false
    },
    type: {
      type: String,
      required: false,
      default: ''
    },
    editFields: {
      type: Object,
      required: false,
      default: () => {return {}}
    },
    hasActionButton: {
      type: Boolean,
      required: false,
      default: false
    }
  }
}
</script>

<style>
.post-creator #inputMedia #media {
  min-height: 190px!important;
}
</style>

<style scoped>
.post-creator {
  background-color: #fff;
  padding: 15px 20px;
}
.product-discount-params .product {
  width: 50%;
}

@media (max-width: 768px) {
  .product-discount-params .product {
    width: 100%;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/standard/input/InputPost.vue?195323bc